import { signIn, signOut, getCurrentUser, signUp, confirmSignUp } from "aws-amplify/auth";

/**
 * Mock OTP storage for demonstration
 * In production, this would use DynamoDB or a backend service
 */
const otpStore = new Map();

/**
 * Send OTP to user's email (passwordless login)
 * @param {string} email - User email
 * @returns {Promise} - Returns OTP sent confirmation
 */
export const sendOTP = async (email) => {
  // Generate a 6-digit OTP
  const otp = Math.floor(100000 + Math.random() * 900000).toString();
  
  // Store OTP with expiration (10 minutes)
  const expiryTime = Date.now() + 10 * 60 * 1000;
  otpStore.set(email, {
    otp,
    expiryTime,
    attempts: 0,
  });
  
  // In production, send via email service (AWS SES, SendGrid, etc.)
  console.log(`🔐 OTP for ${email}: ${otp}`);
  console.log(`⏰ Expires in: 10 minutes`);
  
  // Simulate email sending
  await new Promise((resolve) => setTimeout(resolve, 500));
  
  return {
    success: true,
    email,
    message: "OTP sent to your email. Check console for demo OTP.",
  };
};

/**
 * Verify OTP and authenticate user (passwordless)
 * @param {string} email - User email
 * @param {string} otp - 6-digit OTP
 * @returns {Promise} - Returns authenticated user
 */
export const verifyOTP = async (email, otp) => {
  // Retrieve stored OTP
  const record = otpStore.get(email);
  
  if (!record) {
    throw new Error("OTP expired or not found. Please request a new OTP.");
  }
  
  // Check expiration
  if (Date.now() > record.expiryTime) {
    otpStore.delete(email);
    throw new Error("OTP has expired. Please request a new one.");
  }
  
  // Check max attempts (3 attempts allowed)
  if (record.attempts >= 3) {
    otpStore.delete(email);
    throw new Error("Too many failed attempts. Please request a new OTP.");
  }
  
  // Verify OTP
  if (record.otp !== otp) {
    record.attempts++;
    throw new Error(`Invalid OTP. ${3 - record.attempts} attempts remaining.`);
  }
  
  // OTP is valid, clear it
  otpStore.delete(email);
  
  // In production, create a user session or JWT token
  // For now, simulate successful authentication
  const user = {
    username: email,
    email: email,
    attributes: {
      email: email,
    },
    signInDetails: {
      loginWith: "PASSWORDLESS_OTP",
    },
  };
  
  // Store auth session in localStorage
  localStorage.setItem(
    "amplify_auth_session",
    JSON.stringify({
      user,
      timestamp: Date.now(),
      email,
    })
  );
  
  return user;
};

/**
 * Sign in user with email and password (legacy)
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise} - Returns user object on success
 */
export const loginUser = async (email, password) => {
  const user = await signIn({
    username: email,
    password: password,
  });
  return user;
};

/**
 * Sign out current user
 * @returns {Promise} - Returns void on success
 */
export const logoutUser = async () => {
  try {
    // Clear custom OTP session
    localStorage.removeItem("amplify_auth_session");
    localStorage.removeItem("rememberedEmail");
    
    // Sign out from Cognito if session exists
    await signOut();
  } catch (error) {
    console.error("Logout error:", error);
    // Still clear localStorage even if signOut fails
    localStorage.removeItem("amplify_auth_session");
  }
};

/**
 * Get current authenticated user
 * @returns {Promise} - Returns user object or throws error
 */
export const getAuthenticatedUser = async () => {
  const user = await getCurrentUser();
  return user;
};

/**
 * Check if user is authenticated (Cognito or OTP session)
 * @returns {Promise<boolean>} - Returns true if user is authenticated
 */
export const isUserAuthenticated = async () => {
  try {
    // Check custom OTP session first
    const otpSession = localStorage.getItem("amplify_auth_session");
    if (otpSession) {
      const session = JSON.parse(otpSession);
      // Check if session is not too old (24 hours)
      if (Date.now() - session.timestamp < 24 * 60 * 60 * 1000) {
        return true;
      } else {
        localStorage.removeItem("amplify_auth_session");
      }
    }
    
    // Check Cognito session
    await getCurrentUser();
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Sign up a new user
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {object} userAttributes - Additional user attributes (optional)
 * @returns {Promise} - Returns sign up result
 */
export const registerUser = async (email, password, userAttributes = {}) => {
  const result = await signUp({
    username: email,
    password: password,
    options: {
      userAttributes: {
        email: email,
        ...userAttributes,
      },
    },
  });
  return result;
};

/**
 * Confirm user sign up with verification code
 * @param {string} email - User email
 * @param {string} confirmationCode - Confirmation code from email
 * @returns {Promise} - Returns confirmation result
 */
export const confirmUserSignUp = async (email, confirmationCode) => {
  const result = await confirmSignUp({
    username: email,
    confirmationCode: confirmationCode,
  });
  return result;
};

/**
 * Get error message for specific error codes
 * @param {Error} error - Error object from Cognito
 * @returns {string} - User-friendly error message
 */
export const getErrorMessage = (error) => {
  const errorMessages = {
    UserNotConfirmedException: "Your account is not confirmed. Please check your email.",
    NotAuthorizedException: "Invalid email or password.",
    UserNotFoundException: "User not found. Please check your email.",
    TooManyRequestsException: "Too many login attempts. Please try again later.",
    UsernameExistsException: "This email is already registered.",
    InvalidPasswordException:
      "Password must be at least 8 characters and include uppercase, lowercase, numbers, and special characters.",
    CodeMismatchException: "Invalid verification code.",
    ExpiredCodeException: "Verification code has expired. Please request a new one.",
  };

  return errorMessages[error.code] || error.message || "An error occurred. Please try again.";
};
